import type {
    EntityType,
    PositionSizeRule,
    TradingStrategyForPortfolio,
    UserPortfolio,
    UserPortfolioPosition
} from '@einhaus/project314-types';
import type { StatusTone } from '@/components/UI/StatusBadge';
import { describePositionSizeRule, summarizePositionSizeRule } from '@/utils/capitalRuleFormatters';
import { hasLinkedTradingProfile } from './helpers';

/**
 * Lifecycle of a strategy subscription as the settings table presents it.
 *
 * `blocked` is reserved for a globally inactive strategy. `actionNeeded` covers a subscription that
 * is switched on while another live-entry prerequisite is no longer satisfied.
 */
export type StrategySubscriptionStatus = 'live' | 'blocked' | 'paused' | 'setupNeeded' | 'actionNeeded';

export interface StrategySubscriptionReadiness {
    isOptionsStrategy: boolean;
    hasTradingProfile: boolean;
    hasExitCandidates: boolean;
    hasOptionBuyingStrategies: boolean;
    isStrategyGloballyActive: boolean;
    /** Every prerequisite for new live entries is satisfied. */
    isDeploymentComplete: boolean;
    /** User-facing reasons the subscription cannot go live. Empty when the deployment is complete. */
    blockingReasons: string[];
    /** The subset of `blockingReasons` fixed by configuring this subscription, as opposed to activating the strategy. */
    setupReasons: string[];
    status: StrategySubscriptionStatus;
}

/**
 * Status label for a subscription whose strategy is switched off globally. It names the cause rather
 * than a generic "blocked", so nobody mistakes a subscription that cannot trade for one that will.
 */
export const INACTIVE_STRATEGY_LABEL = 'Strategy inactive';

export const INACTIVE_STRATEGY_REASON = 'The strategy is inactive globally, so it will not trade until it is activated.';

export interface EffectivePositionSizing {
    /** True when the subscription overrides the portfolio defaults. */
    isCustom: boolean;
    rules: PositionSizeRule[];
    /** Headline rule, e.g. "Fixed position size $10,000". */
    summary: string;
    /** Every rule spelled out, for tooltips and the detail panel. */
    ruleDescriptions: string[];
    /** Rules beyond the headline one. */
    extraRuleCount: number;
    /** Neither the subscription nor the portfolio defines sizing, so live entries have nothing to size with. */
    isMissing: boolean;
}

/** The portfolio a subscription belongs to, so rows from several portfolios can share one table. */
export type StrategySubscriptionPortfolio = Pick<UserPortfolio, 'id' | 'name' | 'type' | 'active'>;

/** One fully resolved table row, shared by the desktop table and the mobile card list. */
export interface StrategySubscriptionRow {
    /** Unique across portfolios, since one strategy can be subscribed to several of them. */
    key: string;
    portfolio: StrategySubscriptionPortfolio;
    strategy: TradingStrategyForPortfolio;
    readiness: StrategySubscriptionReadiness;
    sizing: EffectivePositionSizing;
    tradingProfileName: string | null;
    /** The user owns the linked profile, so it can be opened for editing rather than only named. */
    isOwnedTradingProfile: boolean;
    openPositionCount: number;
    /** Effective active flag, optimistic while a toggle is in flight. */
    isActive: boolean;
    isTogglePending: boolean;
}

export const STRATEGY_SUBSCRIPTION_STATUS_LABELS: Record<StrategySubscriptionStatus, string> = {
    actionNeeded: 'Action needed',
    blocked: INACTIVE_STRATEGY_LABEL,
    live: 'Running',
    paused: 'Paused',
    setupNeeded: 'Setup needed'
};

export const STRATEGY_SUBSCRIPTION_STATUS_TONES: Record<StrategySubscriptionStatus, StatusTone> = {
    actionNeeded: 'danger',
    blocked: 'warning',
    live: 'success',
    paused: 'neutral',
    setupNeeded: 'warning'
};

/** Compact chip labels; the long forms in the rule formatters are too wide for a table cell. */
const ENTITY_TYPE_CHIP_LABELS: Record<EntityType, string> = {
    Asset: 'Stocks',
    AssetIndex: 'Index',
    Cryptocurrency: 'Crypto',
    FuturesProduct: 'Futures'
};

export const describeAllowedEntityTypes = (entityTypes: EntityType[] | null | undefined): string[] =>
    entityTypes?.length ? entityTypes.map((entityType) => ENTITY_TYPE_CHIP_LABELS[entityType]) : ['All entities'];

export const isOptionsStrategy = (strategy: TradingStrategyForPortfolio): boolean =>
    strategy.legs.some((leg) => leg.derivativeType === 'Option');

export const describeStrategyInstrumentScope = (strategy: TradingStrategyForPortfolio): 'Options' | 'Underlying' =>
    isOptionsStrategy(strategy) ? 'Options' : 'Underlying';

/**
 * Single source of truth for "can this subscription take live entries", shared by the status badge,
 * the activation switch, and the toggle handler so the table can never offer an action the server
 * would reject.
 */
export const resolveStrategySubscriptionReadiness = (
    strategy: TradingStrategyForPortfolio,
    /** Effective active flag, which may be an in-flight optimistic value rather than `strategy.isActive`. */
    isActive: boolean
): StrategySubscriptionReadiness => {
    const optionsStrategy = isOptionsStrategy(strategy);
    const hasTradingProfile = hasLinkedTradingProfile(strategy.tradingProfileId);
    const hasExitCandidates = (strategy.exitCandidateIds?.length ?? 0) > 0;
    const hasOptionBuyingStrategies = !optionsStrategy || (strategy.optionBuyingStrategyIds?.length ?? 0) > 0;
    const isStrategyGloballyActive = strategy.active;
    const setupReasons: string[] = [];

    if (!hasTradingProfile) setupReasons.push('Link a trading profile.');
    if (!hasOptionBuyingStrategies) setupReasons.push('Choose at least one option buying strategy (OBS).');
    if (!hasExitCandidates) setupReasons.push('Choose at least one exit candidate.');

    const blockingReasons = isStrategyGloballyActive ? setupReasons : [INACTIVE_STRATEGY_REASON, ...setupReasons];

    const isDeploymentComplete = blockingReasons.length === 0;

    return {
        blockingReasons,
        hasExitCandidates,
        hasOptionBuyingStrategies,
        hasTradingProfile,
        isDeploymentComplete,
        isOptionsStrategy: optionsStrategy,
        isStrategyGloballyActive,
        setupReasons,
        status: !isStrategyGloballyActive
            ? 'blocked'
            : isDeploymentComplete
              ? isActive
                  ? 'live'
                  : 'paused'
              : isActive
                ? 'actionNeeded'
                : 'setupNeeded'
    };
};

/**
 * The sizing that will actually be applied to a new entry: the subscription override when present,
 * otherwise the portfolio defaults. The table shows the resolved values rather than only naming the
 * source, so users never have to join two cards in their head.
 */
export const resolveEffectivePositionSizing = (
    strategyRules: PositionSizeRule[] | undefined,
    portfolioRules: PositionSizeRule[]
): EffectivePositionSizing => {
    const isCustom = strategyRules !== undefined;
    const rules = strategyRules ?? portfolioRules;
    const [headlineRule] = rules;

    return {
        extraRuleCount: Math.max(0, rules.length - 1),
        isCustom,
        isMissing: rules.length === 0,
        ruleDescriptions: rules.map(describePositionSizeRule),
        rules,
        summary: headlineRule ? summarizePositionSizeRule(headlineRule) : 'No sizing configured'
    };
};

/**
 * Open positions per strategy. Positions are stored per leg, so trade ids are de-duplicated to keep
 * a multi-leg option position from counting more than once.
 */
export const countOpenPositionsByStrategy = (positions: UserPortfolioPosition[]): Map<number, number> => {
    const openTradeIdsByStrategy = new Map<number, Set<number>>();

    for (const position of positions) {
        if (position.status !== 'open') continue;

        const openTradeIds = openTradeIdsByStrategy.get(position.strategyId);

        if (openTradeIds) {
            openTradeIds.add(position.tradeId);
        } else {
            openTradeIdsByStrategy.set(position.strategyId, new Set([position.tradeId]));
        }
    }

    const countsByStrategy = new Map<number, number>();

    for (const [strategyId, openTradeIds] of openTradeIdsByStrategy) {
        countsByStrategy.set(strategyId, openTradeIds.size);
    }

    return countsByStrategy;
};

export type StrategySubscriptionFilter = 'all' | 'live' | 'blocked' | 'paused' | 'needsSetup';

export const matchesStrategySubscriptionFilter = (status: StrategySubscriptionStatus, filter: StrategySubscriptionFilter): boolean => {
    switch (filter) {
        case 'all':
            return true;
        case 'live':
            return status === 'live';
        case 'blocked':
            return status === 'blocked';
        case 'paused':
            return status === 'paused';
        case 'needsSetup':
            return status === 'setupNeeded' || status === 'actionNeeded';
    }
};

/** Query parameter that makes the portfolio settings page open one subscription's deployment editor on arrival. */
export const CONFIGURE_STRATEGY_PARAM = 'configureStrategy';

export const buildConfigureStrategySubscriptionPath = (portfolioId: number, strategyId: number): string =>
    `/user/portfolioSettings/${portfolioId}?${CONFIGURE_STRATEGY_PARAM}=${strategyId}`;

export const getStrategySubscriptionKey = (portfolioId: number, strategyId: number): string => `${portfolioId}-${strategyId}`;

/** Names and ownership of the trading profiles a subscription can link. */
export interface TradingProfileDirectory {
    /** Owned profiles can be opened for editing rather than only named. */
    ownedTradingProfileIds: ReadonlySet<number>;
    /** Owned and shared profiles, so a subscription linked to a shared profile still shows its name. */
    tradingProfileDisplayNameById: ReadonlyMap<number, string>;
}

/** Activation toggles still in flight, keyed by `getStrategySubscriptionKey`. */
export interface StrategySubscriptionActivationState {
    optimisticActiveByKey: ReadonlyMap<string, boolean>;
    pendingKeys: ReadonlySet<string>;
}

export interface StrategySubscriptionSource {
    portfolio: StrategySubscriptionPortfolio & Pick<UserPortfolio, 'strategiesSubscribed' | 'positions'>;
    /** Portfolio-wide sizing, used to show the effective sizing of subscriptions that do not override it. */
    portfolioPositionSizeRules: PositionSizeRule[];
}

export const buildStrategySubscriptionRows = (
    { portfolio, portfolioPositionSizeRules }: StrategySubscriptionSource,
    { ownedTradingProfileIds, tradingProfileDisplayNameById }: TradingProfileDirectory,
    { optimisticActiveByKey, pendingKeys }: StrategySubscriptionActivationState
): StrategySubscriptionRow[] => {
    const portfolioSummary: StrategySubscriptionPortfolio = {
        active: portfolio.active,
        id: portfolio.id,
        name: portfolio.name,
        type: portfolio.type
    };

    const openPositionCountByStrategy = countOpenPositionsByStrategy(portfolio.positions);

    return (portfolio.strategiesSubscribed ?? []).map((strategy) => {
        const key = getStrategySubscriptionKey(portfolio.id, strategy.id);
        const isActive = optimisticActiveByKey.get(key) ?? strategy.isActive;

        return {
            isActive,
            isOwnedTradingProfile: ownedTradingProfileIds.has(strategy.tradingProfileId),
            isTogglePending: pendingKeys.has(key),
            key,
            openPositionCount: openPositionCountByStrategy.get(strategy.id) ?? 0,
            portfolio: portfolioSummary,
            readiness: resolveStrategySubscriptionReadiness(strategy, isActive),
            sizing: resolveEffectivePositionSizing(strategy.positionSizeRules, portfolioPositionSizeRules),
            strategy,
            tradingProfileName: hasLinkedTradingProfile(strategy.tradingProfileId)
                ? (tradingProfileDisplayNameById.get(strategy.tradingProfileId) ?? `Profile ${strategy.tradingProfileId}`)
                : null
        };
    });
};

export type StrategySubscriptionStatusCounts = Record<Exclude<StrategySubscriptionFilter, 'all'>, number>;

/** Counts per filter bucket, so each filter option can say how many rows it would show. */
export const countStrategySubscriptionStatuses = (rows: readonly StrategySubscriptionRow[]): StrategySubscriptionStatusCounts => {
    const counts: StrategySubscriptionStatusCounts = { blocked: 0, live: 0, needsSetup: 0, paused: 0 };

    for (const row of rows) {
        if (row.readiness.status === 'live') counts.live += 1;
        else if (row.readiness.status === 'blocked') counts.blocked += 1;
        else if (row.readiness.status === 'paused') counts.paused += 1;
        else counts.needsSetup += 1;
    }

    return counts;
};

export const buildStrategySubscriptionFilterOptions = (
    total: number,
    counts: StrategySubscriptionStatusCounts
): { value: StrategySubscriptionFilter; label: string }[] => [
    { value: 'all', label: `All (${total.toLocaleString()})` },
    { value: 'live', label: `Running (${counts.live.toLocaleString()})` },
    { value: 'blocked', label: `${INACTIVE_STRATEGY_LABEL} (${counts.blocked.toLocaleString()})` },
    { value: 'paused', label: `Paused (${counts.paused.toLocaleString()})` },
    { value: 'needsSetup', label: `Needs setup (${counts.needsSetup.toLocaleString()})` }
];

export const describeStrategySubscriptionCounts = (total: number, counts: StrategySubscriptionStatusCounts): string =>
    `${total.toLocaleString()} subscribed · ${counts.live.toLocaleString()} running · ${counts.blocked.toLocaleString()} strategy inactive · ${counts.paused.toLocaleString()} paused · ${counts.needsSetup.toLocaleString()} need setup`;
